import { CloseIcon, GenericAvatar, PermissionsIcon } from 'assets/icons'
import InviteLinkBadge from '../../badges/InviteLinkBadge'
import Button from '../../../components/buttons/Button'
import Switch from '../../../components/form/Switch'
import PopupSectionRow from '../../../components/popup/PopupSectionRow'
import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react'
import {
  addGroupToServer,
  updateGroupSettings
} from '../../../services/api/group'
import { Model } from '../../../types/model'
import { useFilePicker } from 'use-file-picker'
import ColorsPopup from '../ColorsPopup'
import * as Styled from '../styled'
import * as HeaderStyled from '../../sidebar/styled'
import AddGroupProvider, { useAddGroup } from './AddGroupProvider'
import { ErrorLabel } from 'components/labels/styled'
import { GENERIC_ERROR } from 'utils/strings'
import { LoadingSpinner } from 'components/containers/styled'
import { useCalendar } from 'components/providers/CalendarProvider'
import { AccountDoc, GroupDoc } from 'types/api/changes'
import { Box, Grid, TextField } from '@mui/material'
import { defaultGroupImage, featureToMinimumTierNeeded } from 'utils/api/groups'
import Analytics, { EventProperties, Events } from 'analytics/Analytics'
import { AvatarBadge } from 'components/calendar/styled'

import { t } from 'i18next'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import { compressBase64 } from 'utils/file'
import Logger from 'services/Logger'
import MemberCanPopup from './MemberCanPopup'
import { GroupCardAvatarContainer } from 'components/cards/styled'
import { MiscText } from 'components/login/styled'
import i18n from 'i18n'
import Popup from 'components/popup/Popup'
import { TierFeatures } from 'utils/api/group_state'
import CongratsBlock from './CongratsBlock'

interface ContentProps {
  saving: boolean
  setSaving: (b: boolean) => void
  onClose: () => void
  group: Model<GroupDoc> | undefined
  open: boolean
  setOpen: (b: boolean) => void
  titleRef?: React.RefObject<HTMLInputElement>
  account: Model<AccountDoc>
}

interface AddGroupPopupProps extends ContentProps {
  children: ReactNode
  group: Model<GroupDoc> | undefined
  onClickAwayAction?: () => void
}

function Content(props: ContentProps) {
  const [error, setError] = useState<string>('')
  const addGroupProvider = useAddGroup()
  const color = addGroupProvider.color
  const maxLengthForGroupName = 25
  const onChannelChange = (event: ChangeEvent<HTMLInputElement>) => {
    addGroupProvider.setIsCalendarChannel(event.target.checked)
  }

  const onGroupTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError('')
    addGroupProvider.setTitle(event.target.value)
  }

  const toggleCalendarChannel = () => {
    addGroupProvider.setIsCalendarChannel(!addGroupProvider.isCalendarChannel)

    if (props.group !== undefined) {
      updateGroupSettings(
        {
          ...props.group?.GroupSettings
        },
        props.group
      )
    }
  }

  useEffect(() => {
    if (!props.open) {
      //reset form
      addGroupProvider.setTitle('')
      addGroupProvider.setIsCalendarChannel(false)
      addGroupProvider.setPhoto('')
      addGroupProvider.setLink('')
      addGroupProvider.setCreatedGroup(undefined)
    }
  }, [props.open])

  const [showColorPopup, setShowColorPopup] = useState(false)

  const showColors = () => {
    setShowColorPopup(true)
  }

  const [openMemberCanPopup, setOpenMemberCanPopup] = useState(false)

  useEffect(() => {
    props.titleRef?.current?.focus()
  }, [])

  return (
    <div
      style={{
        pointerEvents: props.saving ? 'none' : 'all',
        paddingLeft: 16,
        paddingRight: 16,
        width: '400px'
      }}
    >
      <AddGroupHeader {...props} onError={setError} />

      {addGroupProvider.createdGroup ? (
        <>
          <Box>
            <PopupSectionRow
              onlyTitle
              title={<InviteLinkBadge link={addGroupProvider.link} />}
            />
            {error && <ErrorLabel>{error}</ErrorLabel>}
          </Box>
        </>
      ) : (
        <Box width={'100%'}>
          <Box alignItems={'center'} justifyItems={'center'} width={'100%'}>
            <GroupCardAvatarContainer
              width={72}
              height={72}
              borderWidth={'3px'}
              color={color}
            >
              {props.group && props.group._id.length > 0 ? (
                <Box>
                  <AvatarBadge
                    sx={{ margin: 'unset', width: 62, height: 62 }}
                    name=""
                    asGroup
                    color="red"
                    img={props.group.Photo}
                    emptyPic={defaultGroupImage(props.group)}
                  />
                </Box>
              ) : (
                <AddGroupPhotoButton />
              )}
            </GroupCardAvatarContainer>
          </Box>
          <Box
            marginTop={'16px'}
            width={'100%'}
            justifyContent={'center'}
            alignItems={'center'}
            display={'flex'}
            gap={4}
            sx={{
              pointerEvents:
                props.group && props.group._id.length > 0 ? 'none' : 'all'
            }}
            flexDirection={'row'}
          >
            {addGroupProvider.colors.map((color) => {
              return (
                <GroupCardAvatarContainer
                  key={color}
                  onClick={() => {
                    addGroupProvider.setColor(color)
                  }}
                  borderWidth={'1px'}
                  color={
                    addGroupProvider.color === color ? 'black' : 'transparent'
                  }
                >
                  <Styled.ColorsPopupSelector
                    sx={{ width: 40, height: 40 }}
                    color={color}
                  />
                </GroupCardAvatarContainer>
              )
            })}
            <ColorsPopup
              open={showColorPopup}
              setOpen={setShowColorPopup}
              color={addGroupProvider.color}
              setColor={addGroupProvider.setColor}
              showGenericIcon
            />
          </Box>
          <div
            style={{
              pointerEvents: props.group?._id ? 'none' : 'all',
              marginTop: 16
            }}
          >
            <Styled.AddGroupInputContainer>
              <TextField
                id="group-title-input"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    fontWeight: 500,
                    fontFamily: 'NotoSans'
                  }
                }}
                inputRef={props.titleRef}
                placeholder={`${t('enterCalendarName')}`}
                slotProps={{
                  htmlInput: {
                    maxLength: maxLengthForGroupName
                  }
                }}
                onChange={onGroupTitleChange}
              />
              <Styled.GroupPopupInputCount>{`${
                maxLengthForGroupName - addGroupProvider.title.length
              }`}</Styled.GroupPopupInputCount>
            </Styled.AddGroupInputContainer>
          </div>
          <Box sx={{ pointerEvents: props.group?._id ? 'none' : 'all' }}>
            {props.group && !addGroupProvider.isCalendarChannel && (
              <MemberCanPopup
                open={openMemberCanPopup}
                setOpen={setOpenMemberCanPopup}
                group={props.group}
              >
                <span
                  id="open-members-permission-button"
                  onClick={() => {
                    setOpenMemberCanPopup(true)
                  }}
                >
                  <Box
                    paddingTop={4}
                    paddingBottom={4}
                    flexDirection={'row'}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <PopupSectionRow
                      onlyTitle
                      title={t('calendarPermissions')}
                    />
                    <PermissionsIcon />
                  </Box>
                </span>
              </MemberCanPopup>
            )}
            <Box paddingTop={2} flexDirection={'column'} display={'flex'}>
              <PopupSectionRow
                title={t('setAsCalendarChannel')}
                tierFeature={TierFeatures.CHANNEL}
                neededTier={featureToMinimumTierNeeded(TierFeatures.CHANNEL)}
                value={
                  <Switch
                    checked={addGroupProvider.isCalendarChannel}
                    onChange={onChannelChange}
                  />
                }
                action={toggleCalendarChannel}
              />
              <MiscText fontSize={'12px'}>{t('calendarChannelDesc')}</MiscText>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  )
}

interface AddGroupHeaderProps extends ContentProps {
  onError: (error: string) => void
}

function AddGroupHeader(props: AddGroupHeaderProps) {
  const addGroupProvider = useAddGroup()
  const calendar = useCalendar()

  async function handleGroupSave() {
    if (props.group != undefined && !props.saving) {
      props.titleRef?.current?.blur()
      props.setSaving(true)
      try {
        const group = await addGroupToServer(props.group, addGroupProvider)
        addGroupProvider.setCreatedGroup(group)
        calendar.onGroupServer(group._id ?? '')
        props.setSaving(false)
        addGroupProvider.setIsCalendarChannel(false)
        props.setOpen(false)
        Analytics.getInstance().sendEvent(Events.ADDED_GROUP, {
          [EventProperties.GROUP_NAME]: group.Name,
          [EventProperties.NUM_OF_PARTICIPANTS]: Object.keys(group.Participants)
            .length,
          [EventProperties.ALLOW_META_DATA]:
            addGroupProvider.metaDataState ?? '0',
          [EventProperties.ALLOW_ADD_EDIT_EVENT]:
            addGroupProvider.allCanAddItemsState ?? '0',
          [EventProperties.ALLOW_ADD_PARTICIPANTS]:
            addGroupProvider.addParticipantsState ?? '0',
          [EventProperties.ALLOW_EDIT_THEIR_EVENT]:
            addGroupProvider.addParticipantsState ?? '0',

          [EventProperties.METHOD_OF_SHARING]: EventProperties.METHOD_LINK
        })
      } catch (e) {
        props.setSaving(false)
        props.setOpen(false)
        props.onError(GENERIC_ERROR)
      }
    }
  }

  useEffect(() => {
    const keyEnter = async (event: KeyboardEvent) => {
      if (event.key === 'Enter' && addGroupProvider.title) {
        handleGroupSave()
      }
    }

    document.addEventListener('keydown', keyEnter)

    return () => {
      document.removeEventListener('keydown', keyEnter)
    }
  }, [addGroupProvider.title])

  const closeAddGroup = () => {
    addGroupProvider.setTitle('')
    addGroupProvider.setLink('')
    addGroupProvider.setIsCalendarChannel(false)
    props.onClose()
  }

  return (
    <Box>
      <Grid
        item
        container
        display={'flex'}
        flexDirection={'row'}
        wrap={'nowrap'}
        justifyContent={'space-between'}
      >
        <Styled.PopupCloseButton
          id="add-group-close-button"
          size="sm"
          onClick={closeAddGroup}
        >
          <CloseIcon />
        </Styled.PopupCloseButton>
        {props.group?._id ? (
          <></>
        ) : (
          <>
            <div style={{ opacity: props.saving ? 0 : 1 }}>
              <Button
                id="add-group-save-button"
                disabled={addGroupProvider.title.length === 0}
                size="xs"
                onClick={handleGroupSave}
              >
                {t('save')}
              </Button>
            </div>
            <LoadingSpinner
              loading={props.saving ? 1 : 0}
              style={{
                pointerEvents: props.saving ? 'all' : 'none',

                right: i18n.dir() === 'rtl' ? '90%' : undefined,
                top: '15px'
              }}
            />
          </>
        )}
      </Grid>
      {addGroupProvider.createdGroup && (
        <CongratsBlock
          message={t('yourNewCalendar', { name: addGroupProvider.title })}
          calendarName={addGroupProvider.title}
        />
      )}
    </Box>
  )
}

function AddGroupPhotoButton() {
  const addGroupProvider = useAddGroup()
  const { openFilePicker, filesContent, loading, errors } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  })
  const handleOpenFile = () => {
    openFilePicker()
  }
  useEffect(() => {
    if (filesContent.length > 0)
      compressBase64(filesContent[0].content)
        .then((data) => {
          // returns an array of compressed images
          addGroupProvider.setPhoto(data)
        })
        .catch((e) =>
          Logger.red('Error while compressing image', e, 'AddGroupPopup')
        )
  }, [filesContent])

  return (
    <GroupcalTooltip title={t('addCalendarPhoto')}>
      <Styled.AddGroupPhoto>
        {filesContent.length > 0 ? (
          <span onClick={handleOpenFile}>
            <HeaderStyled.HeaderAvatar
              sx={{ width: 62, height: 62 }}
              name=""
              img={filesContent[0].content}
            />
          </span>
        ) : (
          <GenericAvatar
            width={62}
            height={62}
            onClick={handleOpenFile}
          ></GenericAvatar>
        )}
      </Styled.AddGroupPhoto>
    </GroupcalTooltip>
  )
}

export default function AddGroupPopup(props: AddGroupPopupProps) {
  const { children, ...otherProps } = props

  useEffect(() => {
    const keyEnter = async (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !props.saving) {
        props.onClose()
      }
    }

    document.addEventListener('keydown', keyEnter)

    return () => {
      document.removeEventListener('keydown', keyEnter)
    }
  }, [props.open, props.saving])

  const textFieldRef = useRef<HTMLInputElement>(null)

  return (
    <AddGroupProvider>
      <Popup
        placement="right-start"
        open={props.open}
        setOpen={props.setOpen}
        closeOnTouchOutside={false}
        onClickAwayAction={() => {
          if (props.onClickAwayAction) {
            props.onClickAwayAction()
          } else {
            props.setOpen(false)
            props.onClose()
          }
        }}
        onHide={() => {
          props.setOpen(false)
          props.onClose()
        }}
        content={<Content {...otherProps} titleRef={textFieldRef} />}
      >
        {children}
      </Popup>
    </AddGroupProvider>
  )
}
